/**
 * Logo.tsx -- PhiloFund Brand Logo
 *
 * Renders the official PhiloFund logo exported directly from Figma.
 * Two variants: dark (for light backgrounds) and light (for dark backgrounds).
 * Three sizes: small (nav bar), medium (general), large (login/landing).
 *
 * The logo image files live in /public/brand/ and were pulled from the
 * Figma source file (43fg3D0XMCiKIOe28AoxSS).
 */
import React from 'react';
import { Box } from '@mui/material';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LogoSize = 'small' | 'medium' | 'large';
export type LogoVariant = 'dark' | 'light';

export interface LogoProps {
  size?: LogoSize;
  variant?: LogoVariant;
  sx?: React.ComponentProps<typeof Box>['sx'];
}

// ---------------------------------------------------------------------------
// Size map -- height in px; width scales proportionally
// ---------------------------------------------------------------------------

const HEIGHT_MAP: Record<LogoSize, number> = {
  small: 32,
  medium: 48,
  large: 72,
};

const MAX_WIDTH_MAP: Record<LogoSize, number> = {
  small: 100,
  medium: 152,
  large: 230,
};

// ---------------------------------------------------------------------------
// Logo component
// ---------------------------------------------------------------------------

export const Logo = React.memo(function Logo({
  size = 'medium',
  variant = 'dark',
  sx,
}: LogoProps): JSX.Element {
  const height = HEIGHT_MAP[size];
  const maxWidth = MAX_WIDTH_MAP[size];
  const src = `/brand/logo-${variant}.png`;

  return (
    <Box
      component="img"
      src={src}
      alt="PhiloFund"
      role="img"
      aria-label="PhiloFund"
      sx={{
        height,
        maxWidth,
        width: 'auto',
        objectFit: 'contain',
        display: 'block',
        userSelect: 'none',
        pointerEvents: 'none',
        ...sx,
      }}
    />
  );
});

Logo.displayName = 'Logo';
